/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useContext } from 'react';
import { graphql } from 'react-apollo';

import { compose } from 'recompose';

import { Grid, PageGridContainer, Typography } from '@coursera/cds-core';

import { DomainGetAllQuery } from 'bundles/browse/components/Queries';
import { useMobileOnlyStreamlinedLOHP } from 'bundles/front-page/utils/useStreamlinedLOHP';
import PageLoadEventContext from 'bundles/page/contexts/PageLoadEventContext';

import _t from 'i18n!nls/front-page';

import DomainPill from './DomainPill';

// https://tools.coursera.org/mega/query/_GjvkCHuEequ0VfWCrq7Lg@1#table gives this order for domain popularity
const domainImageMap = [
  {
    id: 'data-science',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/data_science.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/data_science.png',
  },
  {
    id: 'business',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/business.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/business.png',
  },
  {
    id: 'computer-science',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/computer_science.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/computer_science.png',
  },
  {
    id: 'life-sciences',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/health.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/health.png',
  },
  {
    id: 'social-sciences',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/social_sciences.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/social_sciences.png',
  },
  {
    id: 'personal-development',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/personal_development.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/personal_development.png',
  },
  {
    id: 'arts-and-humanities',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/arts_and_humanities.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/arts_and_humanities.png',
  },
  {
    id: 'physical-science-and-engineering',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/physical_science_and_engineering.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/physical_science_and_engineering.png',
  },
  {
    id: 'language-learning',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/language_learning.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/language_learning.png',
  },
  {
    id: 'information-technology',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/information_technology.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/information_technology.png',
  },
  {
    id: 'math-and-logic',
    url: 'https://s3.amazonaws.com/coursera_assets/browse/domains/math_and_logic.png',
    bannerUrl: 'https://s3.amazonaws.com/coursera_assets/browse/domain-banner/math_and_logic.png',
  },
];

type Domain = {
  id: string;
  slug: string;
  name: string;
};

type GraphqlOptionsProps = {
  data?: { DomainsV1Resource?: { domains: { elements: Domain[] } }; loading: boolean };
};

type Props = {
  domainsList: Domain[];
};

const styles = {
  container: css`
    margin: 0 auto;
    margin-bottom: 9rem;
  `,
  title: css`
    margin-bottom: 16px;
  `,
  streamlinedContainer: css`
    margin: 0 auto var(--cds-spacing-800);
  `,
};

const getDomainsToShow = (domainsList: Domain[]) => {
  return domainImageMap.map((domain) => {
    return {
      ...domain,
      ...(domainsList || []).filter((topic) => topic.id === domain.id)[0],
      imageUrl: domain.url,
    };
  });
};

const renderPills = (domainsList: Domain[], hasPageLoaded: boolean) => {
  return getDomainsToShow(domainsList).map((domain) => (
    <DomainPill key={domain.id} isMobile={false} useLazyImage={!hasPageLoaded} {...domain} />
  ));
};

export const DomainPillsContainer = ({ domainsList }: Props) => {
  const { hasPageLoaded } = useContext(PageLoadEventContext);

  const isStreamlinedMWebLOHP = useMobileOnlyStreamlinedLOHP() !== 'control';
  return (
    <section css={isStreamlinedMWebLOHP ? styles.streamlinedContainer : styles.container}>
      <PageGridContainer>
        <Grid item xs={12}>
          <Typography component="h2" variant="d2" css={styles.title}>
            {_t('Explore Coursera')}
          </Typography>
        </Grid>
        <Grid container item xs={12} wrap="wrap" alignItems="stretch">
          {renderPills(domainsList, hasPageLoaded)}
        </Grid>
      </PageGridContainer>
    </section>
  );
};

const DomainGraphqlOptions = {
  options: {
    fetchPolicy: 'cache-first' as const,
  },
  props: ({ data }: GraphqlOptionsProps) => ({
    domainsList: data?.loading ? [] : data?.DomainsV1Resource?.domains?.elements,
  }),
};

export default compose<Props, {}>(graphql(DomainGetAllQuery, DomainGraphqlOptions))(DomainPillsContainer);
